import clsx from "clsx";
import React, { memo, ReactElement, useId, useMemo } from "react";
import { Tooltip } from "react-tooltip";
import { ICON_SIZES } from "../../helpers/constants";
import NotifyDot from "../NotifyDot/NotifyDot";
import { motion } from "framer-motion";
import useMobile from "src/hooks/useMobile";
import { EButtonKind } from "./ButtonTypes";
import { MdHub } from "react-icons/md";

export interface IButton {
  type?: "button" | "reset" | "submit";
  onClick: (e: any) => void;
  kind?: EButtonKind;
  icon?: ReactElement;
  text?: string;
  disabled?: boolean;
  form?: string;
  tooltip?: string;
  active?: boolean;
  className?: string;
  hide?: boolean;
  isLoading?: boolean;
  autoFocus?: boolean;
  theme?: "light" | "dark";
  notify?: boolean;
  animate?: boolean;
}

const Button: React.FC<IButton> = ({
  type = "button",
  onClick,
  kind = "normal",
  text = null,
  icon,
  disabled = false,
  form = "",
  tooltip = "",
  active,
  className = "",
  hide = false,
  isLoading = false,
  autoFocus = false,
  theme = "light",
  notify = false,
  animate = true,
}) => {
  const tooltipId = useId();
  const { isMobile } = useMobile();

  const kindClasses = useMemo(() => {
    switch (kind) {
      case EButtonKind.DANGER: {
        return [
          "bg-red-400 dark:bg-rose-400",
          "text-red-800 dark:text-rose-800",
          "items-center",
          "justify-center",
          "border-red-400 dark:border-rose-400",
          "hover:bg-red-300 dark:hover:bg-rose-300",
          "hover:text-red-800 dark:hover:text-rose-800",
          "focus:bg-red-300 dark:focus:bg-rose-300",
          "focus:text-red-800 dark:focus:text-rose-800",
          "active:bg-red-300 dark:active:bg-rose-300",
          "active:text-red-800 dark:active:text-rose-800",
          "shadow-sm md:shadow-md",
        ];
      }
      case EButtonKind.SAVE: {
        return [
          "bg-blue-500 dark:bg-cyan-400",
          "text-blue-50 dark:text-cyan-800",
          "items-center",
          "justify-center",
          "border-blue-400 dark:border-cyan-400",
          "hover:bg-blue-300 dark:hover:bg-cyan-300",
          "hover:text-blue-800 dark:hover:text-cyan-800",
          "focus:bg-blue-300 dark:focus:bg-cyan-300",
          "focus:text-blue-800 dark:focus:text-cyan-800",
          "active:bg-blue-300 dark:active:bg-cyan-300",
          "active:text-blue-800 dark:active:text-cyan-800",
          "shadow-sm md:shadow-md",
        ];
      }
      case EButtonKind.EDIT: {
        return [
          "bg-yellow-400 dark:bg-indigo-400",
          "text-yellow-800  dark:text-indigo-800",
          "items-center",
          "justify-center",
          "border-yellow-400 dark:border-indigo-400",
          "hover:bg-yellow-300  dark:hover:bg-indigo-300",
          "hover:text-yellow-800 dark:hover:text-indigo-800",
          "focus:bg-yellow-300  dark:focus:bg-indigo-300",
          "focus:text-yellow-800 dark:focus:bg-indigo-800",
          "active:bg-yellow-300  dark:active:bg-indigo-300",
          "active:text-yellow-800  dark:active:text-indigo-800",
          "shadow-sm md:shadow-md",
        ];
      }

      case EButtonKind.NORMAL:
      default: {
        return [
          "bg-white dark:bg-gray-900",
          "text-gray-800 dark:text-gray-200",
          "items-center",
          "justify-center",
          "border-gray-100 dark:border-gray-700",
          "hover:bg-gray-100 dark:hover:bg-gray-800",
          "hover:text-gray-800 dark:hover:text-gray-400",
          "focus:bg-gray-100 dark:hover:bg-gray-800",
          "focus:text-gray-800 dark:hover:text-gray-400",
          "active:bg-gray-100 dark:hover:bg-gray-800",
          "active:text-gray-800 dark:hover:text-gray-400",
          "shadow-sm md:shadow-md dark:shadow-gray-900",
        ];
      }
    }
  }, [kind]);

  if (hide) return null;

  return (
    <>
      <motion.button
        whileHover={{
          ...(animate && !disabled ? { scale: 1.05 } : {}),
        }}
        whileTap={{
          ...(animate && !disabled ? { scale: 0.9 } : {}),
        }}
        transition={{
          ...(animate && !disabled
            ? { type: "spring", stiffness: 400, damping: 17 }
            : {}),
        }}
        disabled={disabled || isLoading}
        type={type}
        data-tooltip-id={tooltipId}
        data-tooltip-content={isMobile ? "" : tooltip}
        data-tooltip-position-strategy="fixed"
        data-tooltip-open-on="hover"
        data-tooltip-offset={0}
        onClick={disabled ? void 0 : onClick}
        autoFocus={autoFocus}
        className={clsx([
          "relative",
          "group",
          "inline-flex",
          "items-center",
          "gap-1",
          {
            "p-2 md:p-3": true,
            "h-[50px] md:h-[50px]": true,
            "min-w-[50px] md:min-w-[50px]": true,
            "opacity-60": disabled,
          },
          "rounded-md",
          "border",
          "md:border",
          "text-sm",
          ...kindClasses,
          className,
        ])}
        {...(form ? { form } : {})}
      >
        {isLoading ? (
          <>
            <div
              className={clsx([
                `h-[${ICON_SIZES.MD}px]`,
                `w-[${ICON_SIZES.MD}px]`,
                "flex",
                "items-center",
                "justify-center",
              ])}
            >
              <MdHub size={ICON_SIZES.MD} className="animate-spin" />
            </div>
          </>
        ) : (
          <>
            {icon && (
              <span
                className={clsx([
                  {
                    "group-hover:animate-pulse": animate && !disabled,
                  },
                ])}
              >
                {icon}
              </span>
            )}
          </>
        )}
        {text && (
          <span className={clsx(["capitalize", "md:text-base"])}>{text}</span>
        )}
        {!!notify && <NotifyDot />}
      </motion.button>
      {!isMobile && (
        <Tooltip
          id={tooltipId}
          style={{ zIndex: 99 }}
          positionStrategy="fixed"
        />
      )}
    </>
  );
};

export default memo(Button);
